import { List, ListItemButton, ListItemText, Menu, TextField, Chip } from '@mui/material';
import React, { FC, memo, useState, useEffect, useCallback } from 'react';
import numberDecorator from 'src/decorators/number.decorator';

export type SelectTruckMenuTruckEntity = {
  id: number;
  licensePlateValue: string;
  truckNumber: string;
  maximumLoadingCapacityWeightForCalculation: number;
  loadingPlatformVolumeMm3: number;
  temporary: boolean;
}

type Props = {
  truckAnchorEl: HTMLElement | null;
  onClose: (event: MouseEvent) => void;
  onTruckClick: (event: React.MouseEvent, id: number, licensePlateValue: string) => void;
  truckData: SelectTruckMenuTruckEntity[];
  truckIdsHasDelivery: number[];
  isTemporary: boolean;
};

const SelectTruckMenuPresenter: FC<Props> = memo(({
  truckAnchorEl,
  onClose,
  onTruckClick,
  truckData,
  truckIdsHasDelivery,
  isTemporary,
}) => {
  const [searchTruckInputValue, setSearchTruckInputValue] = useState('');
  const [searchTruckQuery, setSearchTruckQuery] = useState<string>('');
  const [isComposing, setIsComposing] = useState(false);

  const handleTruckSearchQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTruckInputValue(event.target.value);
  }, []);

  useEffect(() => {
    if (isComposing) return;
    setSearchTruckQuery(searchTruckInputValue);
  }, [isComposing, searchTruckInputValue]);

  const filteredTrucks = truckData?.filter(
    (it) => !it.temporary && it.licensePlateValue.toLocaleLowerCase().includes(searchTruckQuery?.toLowerCase())
  ) ?? [];

  const truckDisplayValue = useCallback((truck: SelectTruckMenuTruckEntity) => {
    if (!truck) return '';

    const weightAndVolume = [
      numberDecorator.convertGramToKg(truck.maximumLoadingCapacityWeightForCalculation),
      numberDecorator.convertMm3ToM3(truck.loadingPlatformVolumeMm3),
    ].filter((it) => it).join('/');
    return [
      truck.licensePlateValue,
      weightAndVolume ? `(${weightAndVolume})` : '',
    ].join(' ');
  }, []);

  const noPlan = <Chip color="error" size="small" label="計画無" />;

  return (
    <Menu
      anchorEl={truckAnchorEl}
      open={Boolean(truckAnchorEl)}
      onClose={onClose}
      sx={{ maxHeight: 500, }}
      PaperProps={{
        style: { padding: 0, margin: 0, }
      }}
    >
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="検索"
        value={searchTruckInputValue}
        onChange={handleTruckSearchQueryChange}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
      />
      <List sx={{ p: 0, m: 0, }}>
        {!isTemporary && (
          <ListItemButton
            key={['truck', '-1'].join('-')}
            onClick={(event: React.MouseEvent) => onTruckClick(event, -1, '指定なし')}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, }}
          >
            <ListItemText
              primary="指定なし"
              primaryTypographyProps={{ fontSize: 14, color: 'black', }}
            />
          </ListItemButton>
        )}
        {filteredTrucks.map((truck) => (
          <ListItemButton
            key={['truck', truck.id].join('-')}
            onClick={(event: React.MouseEvent) => onTruckClick(event, truck.id, truck.licensePlateValue)}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, }}
          >
            <ListItemText
              primary={truckDisplayValue(truck)}
              secondary={truck.truckNumber}
              primaryTypographyProps={{ fontSize: 14, color: 'black', }}
              secondaryTypographyProps={{ fontSize: 12, }}
            />
            {!truckIdsHasDelivery.includes(truck.id) && noPlan}
          </ListItemButton>
        ))}
      </List>
    </Menu>
  );
});

export default SelectTruckMenuPresenter;
